import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { UploadedFile } from "@/api/lctApISchemas";
import { Button } from "@/components/ui/button";
import { Download, FileIcon, ExternalLink, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { CsvPreviewTable } from "./CsvPreviewTable";

interface FilePreviewProps {
  file: UploadedFile;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FilePreview: React.FC<FilePreviewProps> = ({ file, isOpen, onOpenChange }) => {
  const fileName = file.file_name;
  const normalizeFileName = (value: string) => value.split(/[?#]/)[0];
  const matchesFileType = (regex: RegExp) =>
    regex.test(normalizeFileName(file.file_name || "")) || regex.test(normalizeFileName(file.uploaded_file));
  const isImage = matchesFileType(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = matchesFileType(/\.pdf$/i);
  const isCSV = matchesFileType(/\.csv$/i);
  const [downloading, setDownloading] = useState(false);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (isCSV) {
      setCsvLoading(true);
      fetch(file.uploaded_file)
        .then((response) => response.text())
        .then((text) => {
          const rows = text.split("\n").map((row) => row.split(",").map((cell) => cell.trim()));
          setCsvData(rows);
        })
        .catch((error) => console.error("Error loading CSV:", error))
        .finally(() => setCsvLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.uploaded_file]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await fetch(file.uploaded_file);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloading(false);
    }
  };

  const renderContent = () => {
    if (isImage) {
      return (
        <div className="flex h-[60vh] w-full flex-col items-center justify-center gap-4">
          <img src={file.uploaded_file} alt={fileName} className="h-[80%] w-full object-contain" />
        </div>
      );
    }

    if (isPDF) {
      return (
        <div className="flex h-[60vh] w-full flex-col items-center justify-center gap-4">
          {!pdfError ? (
            <iframe src={file.uploaded_file} className="h-[80%] w-full" title={fileName} onError={() => setPdfError(true)} />
          ) : (
            <div className="flex h-[80%] w-full flex-col items-center justify-center bg-gray-100">
              <FileIcon className="h-20 w-20 text-gray-400" />
              <p className="text-lg text-gray-600">
                Unable to preview PDF. Try <b>Open in new tab</b> or <b>Download</b>.
              </p>
            </div>
          )}
        </div>
      );
    }

    if (isCSV) {
      if (csvLoading) {
        return (
          <div className="flex h-[60vh] w-full items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
        );
      }
      if (csvData.length > 0) {
        return (
          <div className="flex w-full flex-col items-center justify-center gap-4 overflow-y-auto">
            <CsvPreviewTable csvData={csvData} />
          </div>
        );
      }
    }

    return (
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <FileIcon className="h-20 w-20 text-gray-400" />
        <p className="text-lg text-gray-600">Preview not available for this file type</p>
        <div className="flex gap-2">
          <Button onClick={handleDownload} variant="outline" className="flex items-center gap-2" disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {downloading ? "" : "Download"}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={`${!isImage ? "max-w-full" : "max-h-[90vh]"}`}>
        <DialogTitle className="text-lg font-semibold">{fileName}</DialogTitle>
        <DialogDescription className="sr-only">File preview dialog</DialogDescription>
        {renderContent()}
        <DialogFooter>
          <div className="flex flex-row gap-2">
            {!isCSV && (
              <Button onClick={() => window.open(file.uploaded_file, "_blank")} className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Open in new tab
              </Button>
            )}

            <Button onClick={handleDownload} variant="outline" className="flex items-center gap-2" disabled={downloading}>
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {downloading ? "" : "Download"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
